"use client";
import { MouseEvent, useCallback } from "react";

import { Text, Tooltip, TooltipFocusableTrigger } from "@zapier/design-system";
import { Icon } from "@zapier/zinnia-icons";

import type { MarkdownCommand } from "../useInsertMarkdownAtSelection";
import styles from "./MarkdownFormattingToolbar.module.css";

const IS_MAC =
  /Mac|iPhone|iPad|iPod/i.test(globalThis.navigator?.platform ?? "") ||
  /Mac OS X/i.test(globalThis.navigator?.userAgent ?? "");

const SHORTCUTS = {
  bold: IS_MAC ? "⌘B" : "Ctrl+B",
  italic: IS_MAC ? "⌘I" : "Ctrl+I",
  link: IS_MAC ? "⌘K" : "Ctrl+K",
  heading1: IS_MAC ? "⌘⌥1" : "Ctrl+Alt+1",
  heading2: IS_MAC ? "⌘⌥2" : "Ctrl+Alt+2",
  heading3: IS_MAC ? "⌘⌥3" : "Ctrl+Alt+3",
  numberedList: IS_MAC ? "⌘⇧7" : "Ctrl+Shift+7",
  bulletedList: IS_MAC ? "⌘⇧8" : "Ctrl+Shift+8",
  checklist: IS_MAC ? "⌘⇧9" : "Ctrl+Shift+9",
  quote: IS_MAC ? "⌘⇧." : "Ctrl+Shift+.",
  code: IS_MAC ? "⌘⇧C" : "Ctrl+Shift+C",
} as const;

const MARKDOWN_HELP_URL =
  "https://help.zapier.com/hc/en-us/articles/43579890272653-Markdown-formatting-in-Zapier";

function QuoteIcon({ size = 16 }: { size?: number }) {
  return (
    <svg
      aria-hidden={true}
      focusable="false"
      fill="currentColor"
      height={size}
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 20 20"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M3.5 2.75a.75.75 0 0 0-1.5 0v14.5a.75.75 0 0 0 1.5 0zM6.75 3a.75.75 0 0 0 0 1.5h8.5a.75.75 0 0 0 0-1.5zM6 10.25a.75.75 0 0 1 .75-.75h10.5a.75.75 0 0 1 0 1.5H6.75a.75.75 0 0 1-.75-.75m.75 5.25a.75.75 0 0 0 0 1.5h7.5a.75.75 0 0 0 0-1.5z"
      />
    </svg>
  );
}

function NumberedListIcon({ size = 16 }: { size?: number }) {
  return (
    <svg
      aria-hidden={true}
      focusable="false"
      height={size}
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 16 16"
      fill="none"
    >
      <path
        fill="currentColor"
        fillRule="evenodd"
        clipRule="evenodd"
        d="M16 5H6V3.5h10zm0 7.5H6V11h10zM.5 1h1.75a.75.75 0 0 1 .75.75V7H1.5V2.5h-1zM0 8.5h2.63C3.39 8.5 4 9.12 4 9.88v1.25c0 .76-.62 1.38-1.37 1.38H1.5v1H4V15H.75a.75.75 0 0 1-.75-.75v-1.87C0 11.62.62 11 1.38 11H2.5v-1H0z"
      />
    </svg>
  );
}

function BulletedListIcon({ size = 16 }: { size?: number }) {
  return (
    <svg
      aria-hidden={true}
      focusable="false"
      height={size}
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 16 16"
      fill="none"
    >
      <path
        fill="currentColor"
        fillRule="evenodd"
        clipRule="evenodd"
        d="M.5 4.25a1.5 1.5 0 1 1 3 0 1.5 1.5 0 0 1-3 0m0 7.5a1.5 1.5 0 1 1 3 0 1.5 1.5 0 0 1-3 0M16 5H6V3.5h10zm0 7.5H6V11h10z"
      />
    </svg>
  );
}

function ChecklistIcon({ size = 16 }: { size?: number }) {
  return (
    <svg
      aria-hidden={true}
      focusable="false"
      height={size}
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 16 16"
      fill="none"
    >
      <path
        fill="currentColor"
        fillRule="evenodd"
        clipRule="evenodd"
        d="M1 3a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2zm2-.5a.5.5 0 0 0-.5.5v10a.5.5 0 0 0 .5.5h10a.5.5 0 0 0 .5-.5V3a.5.5 0 0 0-.5-.5zm9.326 2.98-5 6a.75.75 0 0 1-1.152 0l-2.5-3 1.152-.96L6.75 9.828l4.424-5.308z"
      />
    </svg>
  );
}

function MarkdownIcon({ height = 22 }: { height?: number }) {
  // Keep the original aspect ratio (500x307.69).
  const width = (500 / 307.69) * height;
  return (
    <svg
      aria-hidden={true}
      focusable="false"
      height={height}
      width={width}
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 500 307.69"
    >
      <path
        fill="currentColor"
        d="M463.94,307.69H36.06c-19.88,0-36.06-16.17-36.06-36.06V36.06C0,16.18,16.18,0,36.06,0h427.88c19.88,0,36.06,16.18,36.06,36.06v235.58c0,19.88-16.18,36.06-36.06,36.06ZM36.06,24.04c-6.63,0-12.02,5.39-12.02,12.02v235.58c0,6.63,5.39,12.02,12.02,12.02h427.88c6.63,0,12.02-5.39,12.02-12.02V36.06c0-6.63-5.39-12.02-12.02-12.02H36.06Z"
      />
      <path
        fill="currentColor"
        d="M72.12,235.58V72.12h48.08l48.08,60.1,48.08-60.1h48.08v163.46h-48.08v-93.75l-48.08,60.1-48.08-60.1v93.75h-48.08ZM372.6,235.58l-72.11-79.33h48.08v-84.13h48.08v84.14h48.08l-72.12,79.33Z"
      />
    </svg>
  );
}

interface MarkdownFormattingToolbarProps {
  isDisabled?: boolean;
  onInsert: (command: MarkdownCommand) => void;
  markdownTooltipText?: string;
}

export function MarkdownFormattingToolbar({
  isDisabled = false,
  onInsert,
  markdownTooltipText,
}: MarkdownFormattingToolbarProps) {
  const shortcut = SHORTCUTS;

  const glyph = {
    bold: "B",
    italic: "I",
    heading1: "H1",
    heading2: "H2",
    heading3: "H3",
  } as const;

  const markdownHintText = markdownTooltipText ?? "Markdown is supported.";

  const handleInsert = useCallback(
    (command: MarkdownCommand) => (event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      onInsert(command);
    },
    [onInsert],
  );

  const renderToolbarButton = (
    tooltipText: string,
    tooltipShortcut: string | undefined,
    buttonProps: Omit<
      React.ButtonHTMLAttributes<HTMLButtonElement>,
      "type" | "disabled"
    >,
    children: React.ReactNode,
  ) => (
    <Tooltip
      content={
        tooltipShortcut ? `${tooltipText} (${tooltipShortcut})` : tooltipText
      }
      placement="top"
    >
      <TooltipFocusableTrigger>
        <button
          type="button"
          className={styles.button}
          disabled={isDisabled}
          {...buttonProps}
        >
          {children}
        </button>
      </TooltipFocusableTrigger>
    </Tooltip>
  );

  return (
    <div
      className={`${styles.root}${isDisabled ? ` ${styles.rootDisabled}` : ""}`}
      role="toolbar"
      aria-label="Markdown formatting"
    >
      {renderToolbarButton(
        "Bold",
        shortcut.bold,
        {
          "aria-label": "Bold",
          onMouseDown: handleInsert({
            type: "wrap",
            before: "**",
            after: "**",
          }),
        },
        <Text tag="span" type="Body3" className={styles.glyphBold}>
          {glyph.bold}
        </Text>,
      )}
      {renderToolbarButton(
        "Italic",
        shortcut.italic,
        {
          "aria-label": "Italic",
          onMouseDown: handleInsert({
            type: "wrap",
            before: "_",
            after: "_",
          }),
        },
        <Text tag="span" type="Body3" className={styles.glyphItalic}>
          {glyph.italic}
        </Text>,
      )}
      {renderToolbarButton(
        "Heading 1",
        shortcut.heading1,
        {
          "aria-label": "Heading 1",
          onMouseDown: handleInsert({
            type: "line_prefix",
            prefix: "# ",
            replaceExistingHeadingPrefix: true,
          }),
        },
        <Text tag="span" type="Body3" className={styles.glyphHeading}>
          {glyph.heading1}
        </Text>,
      )}
      {renderToolbarButton(
        "Heading 2",
        shortcut.heading2,
        {
          "aria-label": "Heading 2",
          onMouseDown: handleInsert({
            type: "line_prefix",
            prefix: "## ",
            replaceExistingHeadingPrefix: true,
          }),
        },
        <Text tag="span" type="Body3" className={styles.glyphHeading}>
          {glyph.heading2}
        </Text>,
      )}
      {renderToolbarButton(
        "Heading 3",
        shortcut.heading3,
        {
          "aria-label": "Heading 3",
          onMouseDown: handleInsert({
            type: "line_prefix",
            prefix: "### ",
            replaceExistingHeadingPrefix: true,
          }),
        },
        <Text tag="span" type="Body3" className={styles.glyphHeading}>
          {glyph.heading3}
        </Text>,
      )}
      <span className={styles.divider} aria-hidden={true} />
      {renderToolbarButton(
        "Bulleted list",
        shortcut.bulletedList,
        {
          "aria-label": "Bulleted list",
          onMouseDown: handleInsert({
            type: "line_prefix",
            prefix: "- ",
            insertOnNewLineIfTextBeforeCaret: true,
          }),
        },
        <BulletedListIcon size={16} />,
      )}
      {renderToolbarButton(
        "Numbered list",
        shortcut.numberedList,
        {
          "aria-label": "Numbered list",
          onMouseDown: handleInsert({
            type: "line_prefix",
            prefix: "1. ",
            insertOnNewLineIfTextBeforeCaret: true,
          }),
        },
        <NumberedListIcon size={16} />,
      )}
      {renderToolbarButton(
        "Checklist",
        shortcut.checklist,
        {
          "aria-label": "Checklist",
          onMouseDown: handleInsert({
            type: "line_prefix",
            prefix: "- [ ] ",
            toggleOffPrefixes: ["- [x] ", "- [X] "],
            insertOnNewLineIfTextBeforeCaret: true,
          }),
        },
        <ChecklistIcon size={16} />,
      )}
      <span className={styles.divider} aria-hidden={true} />
      {renderToolbarButton(
        "Link",
        shortcut.link,
        {
          "aria-label": "Link",
          onMouseDown: handleInsert({
            type: "wrap",
            before: "[",
            after: "](url)",
          }),
        },
        <Icon aria-hidden name="actionLink" size={16} />,
      )}
      {renderToolbarButton(
        "Quote",
        shortcut.quote,
        {
          "aria-label": "Quote",
          onMouseDown: handleInsert({
            type: "line_prefix",
            prefix: "> ",
          }),
        },
        <QuoteIcon size={16} />,
      )}
      {renderToolbarButton(
        "Code",
        shortcut.code,
        {
          "aria-label": "Code",
          onMouseDown: handleInsert({
            type: "wrap",
            before: "`",
            after: "`",
          }),
        },
        <Icon aria-hidden name="arrowCode" size={16} />,
      )}
      <span className={styles.markdownHintWrapper}>
        <Tooltip content={markdownHintText} placement="top">
          <TooltipFocusableTrigger>
            <button
              type="button"
              className={styles.markdownHintButton}
              aria-label="Learn Markdown formatting"
              onMouseDown={(event) => {
                // Keep focus on the textarea so its onBlur doesn't flip to Preview before the click fires.
                event.preventDefault();
                event.stopPropagation();
              }}
              onClick={(event) => {
                event.stopPropagation();
                globalThis.window?.open(
                  MARKDOWN_HELP_URL,
                  "_blank",
                  "noopener,noreferrer",
                );
              }}
            >
              <MarkdownIcon height={18} />
            </button>
          </TooltipFocusableTrigger>
        </Tooltip>
      </span>
    </div>
  );
}
